import React, { createContext, useContext, useEffect, useState } from 'react';
import { getWebOrigin } from '../utils/helpers';

const defaultFlags = {
  videoUpload: false,
  videoDisplay: false,
  createSupportTicket: false,
  listSupportTicket: false,
  displayPublicFeeds: false,
  displayGroupFeeds: false,
  displayMarketplace: false,
  liveGameNotification: false,
  // Gates the new src/tournament_formats schedule-generation module's two
  // entry points (Explore Formats button, a tournament's Schedule tab) — see
  // TournamentListPage.jsx / TournamentDetailPage.jsx. Off by default so it
  // ships hidden in production while it's still stabilizing; real
  // tournaments keep using the existing Matches tab (ScheduleTab.jsx) either
  // way. The route itself isn't blocked — this only hides the buttons that
  // link to it, so it stays reachable by direct URL for testing.
  smartSchedule: false,
  // TODO(matches-tab-removal): retire this flag with the tab it gates. The
  // Matches tab is superseded by Schedule; the flag now only provides a way for
  // it to come back, which it did — see TournamentDetailPage.jsx.
  tournamentMatches: false,
  // Global rollout toggle for the Academies module's nav entry (Header.jsx).
  // Not a per-user entitlement — actual licensing enforcement happens
  // server-side at academy-creation time, not here.
  academiesModule: false,
  // Mounts Stripe Checkout inside our own page instead of redirecting to
  // stripe.com. Off by default so the redirect stays the shipped path while
  // this is proven — both flows are live server-side, and the client asks
  // for whichever it can render.
  embeddedCheckout: false,
};

const FeatureFlagsContext = createContext(defaultFlags);

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(defaultFlags);

  useEffect(() => {
    /*
     * On the web this resolves against window.location.origin and goes through
     * the Vite proxy. On a device getWebOrigin() returns
     * REACT_APP_MOBILE_BACKEND_DOMAIN instead, so the URL is only as good as
     * the value baked in at build time — a stale LAN IP there fails here.
     */
    const url = `${getWebOrigin()}${import.meta.env.REACT_APP_IMAGE_HANDLER_CONTEXT_PATH}/features`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setFlags({ ...defaultFlags, ...data }))
      .catch((err) => {
        /*
         * Keep the defaults — every flag off — but say so.
         *
         * This used to swallow the error entirely, and the failure mode was
         * miserable to diagnose: the app looked healthy while whole modules
         * quietly vanished. The Academy tab disappearing on device while it
         * showed fine in the browser was exactly this, and nothing anywhere
         * said the fetch had failed.
         */
        console.warn(`[features] ${url} failed (${err.message}) — all feature flags off`);
      });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
